import { TitlesTyp } from "../../../../../../components/Typography/Typography";
import { FontWeightEnum } from "../../../../../../types/AppTypes";
import {
  DESCRIPTION_LABEL,
  DIMENSION_LABEL,
  ERROR_RANGE_MESSAGE_LABEL,
  EVALUATION_LEVEL_LABEL,
  FINAL_INTERVIEW_LABEL,
  INITIALS_LABEL,
  PERCENTAGE_LABEL,
} from "../../../../../../constants/Dictionary";
import { StyledManagementHeader } from "../../../../Management.styled";
import {
  ManagementTable,
  ManagementTableHeader,
} from "../../../../Components/Table/ManagementTable";
import { FinalInterviewDimensionConfigItem } from "../../../../../../types/api/configurations.type";
import { FieldValues } from "react-hook-form";
import { compareDataGeneric } from "../../../../../../utils/comparing.utils";
import {
  useManagFinalInterview,
  useUpdateManageFinalInterview,
} from "../../../../../../hooks/management/CmSheet/FinalInterview/useManagementFinalInterview.hooks";

type ManageFinalInterviewProps = {
  configYear: number;
};

export const ManageFinalInterview = ({
  configYear,
}: ManageFinalInterviewProps) => {
  const { data, isFetching } = useManagFinalInterview(configYear);
  const {
    mutate: updateManageFinalInterview,
    isPending: isUpdatingFinalInterview,
  } = useUpdateManageFinalInterview();

  const evaluationLevelsColumns: ManagementTableHeader[] = [
    {
      key: "initials",
      label: INITIALS_LABEL,
      width: 15,
      type: "text",
    },
    {
      key: "name",
      label: DIMENSION_LABEL,
      width: 15,
      type: "text",
    },
    {
      key: "range",
      label: PERCENTAGE_LABEL,
      width: 30,
      type: "range",
    },
    {
      key: "description",
      label: DESCRIPTION_LABEL,
      width: 40,
      type: "text",
    },
  ];

  const validateRanges = (
    formData: FieldValues,
  ): { result: boolean; message: string } => {
    const coveredValues = new Set<number>();

    const isValidRanges = formData.items.every(
      (item: FinalInterviewDimensionConfigItem) => {
        if (!Array.isArray(item.range) || item.range.length !== 2) return false;

        const [min, max] = item.range;
        if (min < 0 || max > 100 || min > max) return false;

        for (let i = min; i <= max; i++) {
          if (coveredValues.has(i)) return false;
          coveredValues.add(i);
        }

        return true;
      },
    );

    const validAllValues = Array.from({ length: 101 }, (_, i) => i).every((n) =>
      coveredValues.has(n),
    );

    if (!isValidRanges || !validAllValues) {
      return {
        result: false,
        message: ERROR_RANGE_MESSAGE_LABEL,
      };
    }

    return {
      result: true,
      message: "",
    };
  };

  const onSave = async (formData: FieldValues) => {
    const compareData = (
      formData.items as FinalInterviewDimensionConfigItem[]
    ).map((item, index) => ({
      ...item,
      position: index + 1,
    }));
    const result = compareDataGeneric<FinalInterviewDimensionConfigItem>(
      data!,
      compareData,
    );
    updateManageFinalInterview(result);
  };

  return (
    <>
      <StyledManagementHeader>
        <TitlesTyp variant="2" fontWeight={FontWeightEnum.SEMIBOLD}>
          {FINAL_INTERVIEW_LABEL}
        </TitlesTyp>
      </StyledManagementHeader>
      <ManagementTable<FinalInterviewDimensionConfigItem>
        data={data ?? []}
        keyItem={"name"}
        headers={evaluationLevelsColumns}
        itemLabel={EVALUATION_LEVEL_LABEL}
        draggableRows
        onSave={onSave}
        loading={isFetching || isUpdatingFinalInterview}
        customTest={validateRanges}
      />
    </>
  );
};
